import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '../comps';
import * as Yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import { Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import { Guard } from '../Guard';
import { useCache } from 'src/graphql';
import { UpsertOrganisationMutationVariables, useUpsertOrganisationMutation } from 'src/graphql/_generated';
import { Step1, Step2 } from './forms';
import { Stepper } from 'src/components/Wizzard/comps/Stepper';
import { useAuth } from 'src/state/auth';
import { get } from 'lodash';

const StyledModal = styled(Modal)`
  width: 566px;
  padding: 0;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .btns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
    .btn-prev {
      display: inline-flex;
      align-items: center;
      margin-right: auto;
      cursor: pointer;
      text-decoration: underline;
      text-decoration-color: ${({ theme }) => theme.colors.prussianBlue};
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface OrganisationCreateModalV2Props {
  onClose: () => void;
}

export const OrganisationCreateModalV2: React.FC<OrganisationCreateModalV2Props> = ({ onClose }) => {
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const { refetchQuery } = useCache();

  const { profile } = useAuth();

  const validationSchema = Yup.object({
    name: Yup.string().required('Required').max(200, 'Max 200 characters'),
    domain: Yup.string()
      .required('Required')
      .matches(
        /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
        'Email domain should be a valid domain format (e.g., domain.com)',
      ),
    primaryContact: Yup.string().required('Required').email('Invalid email address').max(100, 'Max 100 characters'),
    billingContact: Yup.string().nullable().email('Invalid email address').max(100, 'Max 100 characters'),
  });

  const [create] = useUpsertOrganisationMutation({
    onCompleted: () => {
      refetchQuery({ include: ['profile', 'flatOrganisations'] });
      onClose();
    },
    onError: (err: any) => {
      setErr(err.message || 'Something went wrong');
    },
  });

  const formik = useFormik({
    initialValues: {} satisfies UpsertOrganisationMutationVariables as UpsertOrganisationMutationVariables,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      console.log('====CREATE ORGANISATION====', values);
      return create({
        variables: values,
      });
    },
  });

  const { dirty, handleSubmit, values, errors, isValid, isSubmitting } = formik;

  const selectedSettingsOption = profile?.activeOrganisation?.actionsOnThisOrganisation?.settingsOptions.find(
    (o) => o.organisationType === values.organisationType,
  );

  const canNextStep = !get(errors, 'domain') && !get(errors, 'primaryContact') && !get(errors, 'name');

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen>
          <Spacer $px={40} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              Create an Organisation
            </Poppins>
            <Spacer $px={20} />
            <div style={{ width: 200 }}>
              <Stepper
                slides={[
                  { title: '', id: '1' },
                  { title: '', id: '2', locked: !canNextStep },
                ]}
                maxSteps={2}
                step={step - 1}
                onChangeSlide={(id) => setStep(Number(id) as 1 | 2)}
              />
            </div>
            <Spacer $px={20} />
            <FormikProvider value={formik}>
              {step === 1 && <Step1 />}
              {step === 2 && <Step2 selectedSettingsOption={selectedSettingsOption} />}
            </FormikProvider>
            <Spacer $px={40} />
            {step === 1 && (
              <Button onClick={() => setStep(2)} primary style={{ width: '100%' }} disabled={!canNextStep}>
                PROCEED
              </Button>
            )}
            {step === 2 && (
              <div className="btns">
                <div className="btn-prev" onClick={() => setStep(1)}>
                  <Poppins weight={500}>Previous</Poppins>
                </div>
                <Button onClick={() => handleSubmit()} primary disabled={!isValid || isSubmitting}>
                  Create
                </Button>
              </div>
            )}

            {!!err && (
              <>
                <Spacer $px={15} />
                <div className="error">{err}</div>
              </>
            )}
          </div>

          <Spacer $px={40} />
        </StyledModal>
      }
    />
  );
};
